import * as Sentry from '@sentry/react';
import { useState, useEffect, createElement, Fragment } from 'react';

// Get the environment from the environment variables
const environment = import.meta.env.MODE || 'development';

// Check if Sentry should be enabled
export const isSentryEnabled = Boolean(import.meta.env.VITE_SENTRY_DSN);

// Initialize Sentry
if (isSentryEnabled) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    integrations: [Sentry.browserTracingIntegration()],
    tracesSampleRate: 1.0, // Capture 100% of transactions for development, adjust for production
    environment: environment,
    // Allow sending PII data to Sentry
    sendDefaultPii: true,
    // Customize the beforeSend function to add user context if available
    beforeSend(event) {
      // You can modify the event here before it's sent to Sentry
      return event;
    },
  });
} else {
  console.warn(
    'Sentry DSN not provided. Client-side error tracking is disabled.'
  );
}

// Utility function to set user information
export function setSentryUser(user: {
  id?: string;
  email?: string;
  username?: string;
}) {
  if (isSentryEnabled) {
    Sentry.setUser(user);
  }
}

// Utility function to add breadcrumb
export function addSentryBreadcrumb(
  message: string,
  category = 'user-action',
  level: Sentry.Breadcrumb['level'] = 'info',
  data?: Record<string, unknown>
) {
  if (isSentryEnabled) {
    Sentry.addBreadcrumb({
      message,
      category,
      level,
      data,
    });
  }
}

// Function to set a transaction ID for the current scope
export function setSentryTransactionId(transactionId: string) {
  if (isSentryEnabled) {
    // Add transaction ID as a tag
    Sentry.setTag('transaction_id', transactionId);
    // Add transaction context
    Sentry.setContext('transaction', {
      id: transactionId,
      timestamp: new Date().toISOString(),
    });
  }
}

// Generate a unique transaction ID for request tracing
export function generateTransactionId(): string {
  return `txid-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
}

// Utility function to capture an exception with additional context
export function captureException(
  error: unknown,
  context?: Record<string, unknown>
) {
  if (isSentryEnabled) {
    if (context) {
      Sentry.withScope((scope) => {
        Object.entries(context).forEach(([key, value]) => {
          scope.setExtra(key, value);
        });
        Sentry.captureException(error);
      });
    } else {
      Sentry.captureException(error);
    }
  } else {
    // Log to console when Sentry is disabled
    console.error('Error captured (Sentry disabled):', error);
    if (context) {
      console.error('Error context:', context);
    }
  }
}

// Create a fallback error boundary component for when Sentry is disabled
function FallbackErrorBoundary({
  children,
  fallback,
}: {
  children: React.ReactNode;
  fallback: React.ComponentType<{ error: Error }>;
}) {
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    // Add an error listener for uncaught errors
    const handleError = (event: ErrorEvent) => {
      console.error('Error caught by fallback error boundary:', event.error);
      setError(event.error || new Error('Unknown error occurred'));
    };

    window.addEventListener('error', handleError);
    return () => window.removeEventListener('error', handleError);
  }, []);

  if (error) {
    const FallbackComponent = fallback;
    return createElement(FallbackComponent, { error });
  }

  return createElement(Fragment, null, children);
}

// Export a custom component for the App that wraps the correct error boundary
export function SentryErrorBoundary(props: {
  children: React.ReactNode;
  fallback: React.ComponentType<{ error: Error }>;
}) {
  if (isSentryEnabled) {
    // Convert the component type to a fallback render function
    const fallbackFn = (errorData: {
      error: unknown;
      componentStack: string;
      eventId: string;
      resetError(): void;
    }) => {
      const FallbackComponent = props.fallback;
      return createElement(FallbackComponent, {
        error:
          errorData.error instanceof Error
            ? errorData.error
            : new Error(String(errorData.error)),
      });
    };

    return createElement(Sentry.ErrorBoundary, {
      fallback: fallbackFn,
      children: props.children,
    });
  } else {
    return createElement(FallbackErrorBoundary, {
      fallback: props.fallback,
      children: props.children,
    });
  }
}
